// Пошук отримувача за номером картки серед користувачів застосунку:
// основна картка — users/{chatId}/cardNumber (пише бот), додаткові —
// cards/{chatId}/{cardId}/number.
//
// Для users запит іде через orderByChild('cardNumber') — він працює лише з
// індексом у правилах БД: "users": { ".indexOn": ["cardNumber"] }. Без
// індексу Firebase відхиляє запит («Index not defined»), тоді беремо список
// chatId через REST ?shallow=true і читаємо лише cardNumber кожного
// (а не цілих користувачів з аватарами).
import { ref, get, query, orderByChild, equalTo } from 'firebase/database';
import { database } from './firebaseConfig';
import { MAIN_CARD_ID, sortByOrder } from './cardsStore';

// ПІБ у форматі «Прізвище Ім'я» → «Ім'я П.», як у monobank
export function shortName(pib) {
  const [surname, firstName] = (pib || '').trim().split(/\s+/);
  if (!firstName) return surname || '';
  return `${firstName} ${surname[0].toUpperCase()}.`;
}

async function userByChatId(chatId, cardId, card) {
  const snap = await get(ref(database, `users/${chatId}`));
  const user = snap.val() || {};
  return {
    chatId, cardId, cardType: card.type, currency: card.currency || 'UAH',
    name: shortName(user.pib), photo: user.photoUrl || '',
  };
}

async function chatIdByMainCard(number) {
  try {
    const snap = await get(query(ref(database, 'users'), orderByChild('cardNumber'), equalTo(number)));
    return snap.exists() ? Object.keys(snap.val())[0] : null;
  } catch (e) {
    // немає індексу — перебираємо самі
  }
  const res = await fetch(`${database.app.options.databaseURL}/users.json?shallow=true`);
  const ids = Object.keys((await res.json()) || {});
  const numbers = await Promise.all(ids.map((id) => get(ref(database, `users/${id}/cardNumber`))
    .then((s) => String(s.val() || '').replace(/\D/g, ''))
    .catch(() => '')));
  const i = numbers.indexOf(number);
  return i === -1 ? null : ids[i];
}

// number — 16 цифр без пробілів. null — не знайдено.
// Результат: { chatId, cardId, cardType, currency, name, photo }
export async function findUserByCard(number) {
  const mainChatId = await chatIdByMainCard(number);
  if (mainChatId) return userByChatId(mainChatId, 'black', { type: 'black', currency: 'UAH' });

  const cardsSnap = await get(ref(database, 'cards'));
  const all = cardsSnap.val() || {};
  for (const chatId of Object.keys(all)) {
    for (const cardId of Object.keys(all[chatId] || {})) {
      if (String(all[chatId][cardId]?.number || '').replace(/\D/g, '') === number) {
        return userByChatId(chatId, cardId, all[chatId][cardId]);
      }
    }
  }
  return null;
}

// Гривневі картки отримувача для вибору «На … картку»: основна (чорна) +
// відкриті в застосунку, не заблоковані, у порядку з «Усі картки».
// [{ id, type, name, number, currency }]
export async function loadRecipientCards(chatId) {
  const [numSnap, cardsSnap, orderSnap] = await Promise.all([
    get(ref(database, `users/${chatId}/cardNumber`)),
    get(ref(database, `cards/${chatId}`)),
    get(ref(database, `cardsOrder/${chatId}`)),
  ]);
  const main = {
    id: MAIN_CARD_ID, type: 'black', name: 'Чорна картка', number: String(numSnap.val() || ''), currency: 'UAH',
  };
  const extra = Object.entries(cardsSnap.val() || {})
    .map(([id, c]) => ({
      id, type: c.type, name: c.name, number: String(c.number || ''), currency: c.currency || 'UAH', status: c.status, createdAt: c.createdAt || 0,
    }))
    .sort((a, b) => a.createdAt - b.createdAt);
  const order = Array.isArray(orderSnap.val()) ? orderSnap.val().filter(Boolean) : [];
  return sortByOrder([main, ...extra], order).filter((c) => c.currency === 'UAH' && c.status !== 'blocked');
}
